import { createSignal, createEffect, createContext, useContext } from 'solid-js'
import STButton, { ButtonType } from '../st-button/st-button';
import STInput from '../st-input/st-input';
import STRange from '../st-range-slider/st-range-slider';
import STAlert from '../st-alert/st-alert';
import { MyWritingStyleModal } from '../st-memo-generator-btn/MyWritingStyleModal';
import { smGenerator as api } from 'st-app/api'
import { Verbosity } from 'st-app/api';
import STModal from '../st-modal/st-modal';
import './sm-generator.less';
import styles from './sm-generator.module.css';
import { GeneratedTextPreview } from '../st-memo-generator-btn/st-generated-text-preview';
import { MemoStoreContext } from '../st-memo-generator-btn/st-memo-generator-btn';
import { MemoStore } from '../st-memo-generator-btn/MemoStore';

const ST = (window as any).ST || {};

class STGeneratorEvents {
    onReplace?: (text: string) => void;
    onClose?: () => void;
}

export class SMGeneratorOptions {
    text: string = '';
    show: boolean = false;
    events: STGeneratorEvents = new STGeneratorEvents();
}

export default function SMGenerator(props: SMGeneratorOptions) {
    const [showModal, setShowModal] = createSignal(props.show);

    createEffect(() => {
        setShowModal(props.show);
    });

    createEffect(() => {
        if (showModal()) {
            setGeneratedText('');
        }
        else {
            if (props.events.onClose) {
                props.events.onClose();
            }
        }
    });

    const memoStore = new MemoStore();
    const showWritingConfigModal = createSignal(false);
    const selectedWritingStyle = createSignal(memoStore.myWritingStyle().writingStyleGU);

    const [generatedText, setGeneratedText] = createSignal('');
    const [loading, setLoading] = createSignal(false);

    // maintain a local version of verbosity
    const [currVerbosity, setCurrVerbosity] = createSignal(memoStore.myWritingStyle().verbosity);

    // When we update myWritingStyle, also update the verbosity slider
    createEffect(() => {
        const writingStyle = memoStore.myWritingStyle();

        // ensure we only trigger this when myWritingStyle changes
        if (writingStyle) {
            requestAnimationFrame(() => {
                if (writingStyle.verbosity !== currVerbosity()) {
                    setCurrVerbosity(writingStyle.verbosity);
                }
            });
        }
    });

    // Flip it, and when we update the verbosity slider, we also update myWritingStyle
    createEffect(() => {
        const verbosity = currVerbosity();

        // ensure we only trigger this when myWritingStyle changes
        requestAnimationFrame(() => {
            const writingStyle = memoStore.myWritingStyle();
            if (writingStyle.verbosity !== verbosity) {
                writingStyle.verbosity = verbosity;
                memoStore.setMyWritingStyle(writingStyle);
            }
        });
    });

    const [hintText, setHintText] = createSignal('');

    createEffect(() => {
        setHintText(props.text);
    });

    return <MemoStoreContext.Provider value={memoStore}>
        <STModal class={`${styles['st-memo-gen']} sm-generator`} show={[showModal, setShowModal]}>
            <STAlert type="info">What would you like to say?</STAlert>
            <div class="flexbox horizontal middle">
                <span class="auto" style="margin: 0 5px;">
                    <STInput value={[hintText, setHintText]} onEnterKey={generateResponses} />
                </span>
                <STButton type="primary" onClick={generateResponses}>
                    Compose
                </STButton>
            </div>

            <STRange type={Verbosity} value={[currVerbosity, setCurrVerbosity]} label="Verbosity" />

            <div class="auto flexbox horizontal">
                <GeneratedTextPreview onReplace={() => onReplace(generatedText())} loading={loading()} generatedText={generatedText()} />
            </div>

            <div class="flexbox horizontal middle padded">
                <div>
                    <STButton onClick={() => showWritingConfigModal[1](true)} tooltip="Configure My Writing Style">
                        <i class="fas fa-pen-nib"></i>
                    </STButton>
                </div>
                <div class="auto"></div>
                <div>
                    <STButton onClick={() => setShowModal(false)}>Close</STButton>
                </div>
            </div>

            <MyWritingStyleModal show={showWritingConfigModal}></MyWritingStyleModal>
        </STModal>
    </MemoStoreContext.Provider>;

    function onReplace(text: string) {
        if (props.events.onReplace) {
            props.events.onReplace(text);
        }

        setGeneratedText(text);
        setShowModal(false);
    }

    async function generateResponses() {
        const style = memoStore.getWritingStyle(selectedWritingStyle[0]());

        setLoading(true);
        setGeneratedText('');
        
        api.Generate(hintText(), style)
            .then((res) => new Promise<any>(r => setTimeout(() => r(res), 1000)))
            .then(res => {
                setGeneratedText(res.text);
                setLoading(false);
            });
    }
}
SMGenerator.defaultProps = new SMGeneratorOptions();